import { useState } from "react";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import "./styles.css";

// import required modules
import { EffectFade, Pagination, Autoplay } from "swiper/modules";
import { Box, Button, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { Link } from "react-router-dom";
import Banner from "../../Banner";
import { slideTimeOut, styles } from "../../../assets/styles";
import { topSlideData } from "../../../utils/data";

// ── Existing keyframes ──────────────────────────────────────────────────────
const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const slideLeftToRight = keyframes`
  from { transform: translateX(-140%); }
  to { transform: translateX(140%); }
`;

const slideRightToLeft = keyframes`
  from { transform: translateX(140%); }
  to { transform: translateX(-140%); }
`;

const floatBubbleUp = keyframes`
  0% { transform: translateY(0) scale(0.8); opacity: 0; }
  10% { opacity: 0.6; }
  90% { opacity: 0.6; }
  100% { transform: translateY(-120vh) scale(1.2); opacity: 0; }
`;

const floatBubbleDown = keyframes`
  0% { transform: translateY(0) scale(0.8); opacity: 0; }
  10% { opacity: 0.6; }
  90% { opacity: 0.6; }
  100% { transform: translateY(120vh) scale(1.2); opacity: 0; }
`;

const floatBubbleRight = keyframes`
  0% { transform: translateX(0) scale(0.8); opacity: 0; }
  10% { opacity: 0.6; }
  90% { opacity: 0.6; }
  100% { transform: translateX(120vw) scale(1.2); opacity: 0; }
`;

const floatBubbleLeft = keyframes`
  0% { transform: translateX(0) scale(0.8); opacity: 0; }
  10% { opacity: 0.6; }
  90% { opacity: 0.6; }
  100% { transform: translateX(-120vw) scale(1.2); opacity: 0; }
`;

// ── New keyframes for mobile hero ───────────────────────────────────────────
const rotateSlow = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.6; }
  100% { transform: scale(2.6); opacity: 0; }
`;

const mobileFadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.38; }
  50%       { opacity: 0.75; }
`;

const shimmerLine = keyframes`
  0%   { transform: translateX(-100%) skewX(-12deg); }
  100% { transform: translateX(300%) skewX(-12deg); }
`;

const sonarRing = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.55); opacity: 0.55; }
  100% { transform: translate(-50%, -50%) scale(2.2);  opacity: 0; }
`;

const iconEntrance = keyframes`
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.7); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const zoomPulse = keyframes`
  0%, 60%, 100% { transform: scale(1);    opacity: 0.7; }
  30%            { transform: scale(1.22); opacity: 1;   }
`;

const chipMarquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const chipZoom = keyframes`
  0%, 22%, 100% { transform: scale(1);   opacity: 0.65; }
  11%            { transform: scale(1.5); opacity: 1;    }
`;

// ── Per-slide visual config for mobile ─────────────────────────────────────
const mobileVisuals = {
  "Services": {
    icon: "⚡",
    iconBg: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
    glow: "rgba(91,124,250,0.55)",
    accentGlow: "rgba(118,75,162,0.3)",
    chips: ["🤖", "💻", "📱", "☁️", "🔒", "⚙️"],
  },
  "Talent Management": {
    icon: "👥",
    iconBg: "linear-gradient(135deg, #8b5cf6 0%, #5b7cfa 100%)",
    glow: "rgba(139,92,246,0.55)",
    accentGlow: "rgba(91,124,250,0.3)",
    chips: ["📊", "🎯", "💼", "🤝", "🏅", "🌍"],
  },
  "Our Top Products": {
    icon: "📦",
    iconBg: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)",
    glow: "rgba(59,130,246,0.55)",
    accentGlow: "rgba(6,182,212,0.3)",
    chips: ["🛒", "🎾", "🧑‍💼", "📅", "📈", "🚀"],
  },
  "Industries We Cover": {
    icon: "🏢",
    iconBg: "linear-gradient(135deg, #764ba2 0%, #5b7cfa 100%)",
    glow: "rgba(118,75,162,0.55)",
    accentGlow: "rgba(91,124,250,0.3)",
    chips: ["🛍️", "🏥", "💳", "📞", "🏭", "✈️"],
  },
};

export default function SliderCarousal() {
  const [activeIndex, setActiveIndex] = useState(0);

  const handleSlideChange = (swiper) => {
    setActiveIndex(swiper.realIndex);
  };

  const pagination = {
    clickable: true,
    renderBullet: function (index, className) {
      return (
        '<div class="' +
        className +
        '"> <span class="pagination-text">' +
        topSlideData[index].content.title +
        "</span></div>"
      );
    },
  };

  return (
    <Box sx={{ mt: { xs: -8.125, sm: -10 } }}>
      <Box
        sx={{
          ...styles.bannerBg,
          ...styles.sliderStyles,
          ...styles.sliderPagination,
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          "& .swiper": {
            background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          },
          "& .swiper-slide": {
            background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          },
        }}
      >
        <Swiper
          loop={true}
          onSlideChange={handleSlideChange}
          pagination={pagination}
          autoplay={{
            delay: slideTimeOut,
            disableOnInteraction: false,
          }}
          effect={"fade"}
          navigation={true}
          modules={[Autoplay, EffectFade, Pagination]}
          className="mySwiper"
        >
          {topSlideData.map((item) => {
            const mv = mobileVisuals[item.content.title] || mobileVisuals["Services"];

            return (
              <SwiperSlide key={item.id}>
                {/* Background zoom box */}
                <Box
                  sx={{
                    background: "transparent",
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    justifyContent: "flex-start",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "100%",
                    backgroundSize: "cover",
                    animationName: "zoomInEffect",
                    animationDuration: "45000ms",
                    animationTimingFunction: "ease",
                    animationFillMode: "both",
                    animationPlayState: "running",
                    animationIterationCount: "infinite",
                  }}
                  loading="lazy"
                />
                {/* Gradient overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage:
                      "linear-gradient(135deg, rgba(248, 250, 252, 0.2) 0%, rgba(240, 244, 255, 0.2) 100%)",
                    pointerEvents: "none",
                  }}
                />

                {/* ══════════════════════════════════════════
                    MOBILE HERO — xs only, dark overlay
                    ══════════════════════════════════════════ */}
                <Box
                  sx={{
                    display: { xs: "flex", sm: "none" },
                    position: "absolute",
                    inset: 0,
                    zIndex: 5,
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    pt: "108px",
                    px: "22px",
                    pb: "20px",
                    background:
                      "linear-gradient(160deg, #312e81 0%, #6d28d9 48%, #a855f7 100%)",
                    overflow: "hidden",
                  }}
                >
                  {/* Dot-grid texture */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage:
                        "radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px)",
                      backgroundSize: "22px 22px",
                      pointerEvents: "none",
                    }}
                  />

                  {/* Top-right accent glow */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -60,
                      right: -60,
                      width: 220,
                      height: 220,
                      borderRadius: "50%",
                      background: `radial-gradient(circle, ${mv.glow} 0%, transparent 65%)`,
                      filter: "blur(10px)",
                      pointerEvents: "none",
                    }}
                  />

                  {/* Bottom-left accent glow */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "12%",
                      left: -60,
                      width: 180,
                      height: 180,
                      borderRadius: "50%",
                      background: `radial-gradient(circle, ${mv.accentGlow} 0%, transparent 65%)`,
                      filter: "blur(8px)",
                      pointerEvents: "none",
                    }}
                  />

                  {/* Shimmer sweep line */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "38%",
                      left: 0,
                      width: "40%",
                      height: "1px",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
                      animation: `${shimmerLine} 5s ease-in-out infinite 1.5s`,
                      pointerEvents: "none",
                    }}
                  />

                  {/* Pill badge */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "7px",
                      px: "16px",
                      py: "7px",
                      borderRadius: "999px",
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.16)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      mb: "11px",
                      flexShrink: 0,
                      animation: `${mobileFadeUp} 0.55s ease-out 0.1s both`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: "#93c5fd",
                        flexShrink: 0,
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          inset: 0,
                          borderRadius: "50%",
                          background: "#93c5fd",
                          animation: `${pulseRing} 1.8s ease-out infinite`,
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.88)",
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "2.2px",
                        textTransform: "uppercase",
                        lineHeight: 1,
                      }}
                    >
                      {item.content.title}
                    </Typography>
                  </Box>

                  {/* Heading */}
                  <Typography
                    sx={{
                      fontSize: "24px",
                      fontWeight: 800,
                      color: "#fff",
                      textAlign: "center",
                      lineHeight: 1.28,
                      mb: "9px",
                      letterSpacing: "-0.4px",
                      flexShrink: 0,
                      animation: `${mobileFadeUp} 0.55s ease-out 0.2s both`,
                    }}
                  >
                    {item.content.heading}
                  </Typography>

                  {/* Subtext */}
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "rgba(255,255,255,0.75)",
                      textAlign: "center",
                      lineHeight: 1.6,
                      mb: "16px",
                      flexShrink: 0,
                      animation: `${mobileFadeUp} 0.55s ease-out 0.3s both`,
                    }}
                  >
                    {item.content.textContent}
                  </Typography>

                  {/* ── Emoji marquee strip ── */}
                  <Box
                    sx={{
                      mx: "-22px",
                      overflow: "hidden",
                      position: "relative",
                      height: 68,
                      mt: "12px",
                      flexShrink: 0,
                      animation: `${mobileFadeUp} 0.55s ease-out 0.55s both`,
                    }}
                  >
                    {/* Left fade */}
                    <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 56, background: "linear-gradient(to right, #312e81 30%, transparent)", zIndex: 2, pointerEvents: "none" }} />
                    {/* Right fade */}
                    <Box sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 56, background: "linear-gradient(to left, #a855f7 30%, transparent)", zIndex: 2, pointerEvents: "none" }} />

                    {/* Scrolling row — duplicated for seamless -50% loop */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        pl: "12px",
                        width: "max-content",
                        height: "100%",
                        animation: `${chipMarquee} 8s linear infinite`,
                      }}
                    >
                      {[...mv.chips, ...mv.chips].map((chip, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 54,
                            height: 54,
                            borderRadius: "14px",
                            background: "rgba(255,255,255,0.1)",
                            border: "1px solid rgba(255,255,255,0.18)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "26px",
                            flexShrink: 0,
                            animation: `${chipZoom} 8s ease-in-out infinite ${(i % 6) * (8 / 6)}s`,
                          }}
                        >
                          {chip}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Bottom section — Explore button + slide label, pinned to bottom */}
                  <Box sx={{ position: "absolute", bottom: "16px", left: 0, right: 0, px: "22px" }}>
                    {/* CTA Button — 25px above slide label */}
                    {item.content.buttonText && (
                      <Box
                        component={Link}
                        to={item.content.buttonUrl}
                        sx={{ display: "block", textDecoration: "none", mb: "40px" }}
                      >
                        <Button
                          variant="contained"
                          fullWidth
                          disableElevation
                          sx={{
                            background: mv.iconBg,
                            color: "#fff",
                            fontWeight: 800,
                            py: "14px",
                            borderRadius: "15px",
                            fontSize: "15px",
                            textTransform: "none",
                            letterSpacing: "0.2px",
                            boxShadow: `0 12px 32px ${mv.glow}`,
                            border: "1px solid rgba(255,255,255,0.14)",
                            transition: "all 0.28s ease",
                            "&:hover": { opacity: 0.88, transform: "translateY(-2px)", boxShadow: `0 18px 40px ${mv.glow}` },
                            "&:active": { transform: "scale(0.98)" },
                          }}
                        >
                          {item.content.buttonText}&nbsp;&nbsp;→
                        </Button>
                      </Box>
                    )}
                    {/* Animated progress line */}
                    <Box sx={{ position: "relative", height: "3px", borderRadius: "2px", background: "rgba(255,255,255,0.2)", mb: "10px", overflow: "hidden" }}>
                      <Box key={activeIndex} sx={{ position: "absolute", left: 0, top: 0, height: "100%", borderRadius: "2px", background: "rgba(255,255,255,0.9)", animation: `growWidth ${slideTimeOut}ms linear forwards`, "@keyframes growWidth": { "0%": { width: "0%" }, "100%": { width: "100%" } } }} />
                    </Box>
                    <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "14px", fontWeight: 700, textAlign: "center", letterSpacing: "0.5px" }}>
                      {topSlideData[activeIndex]?.content?.title}
                    </Typography>
                  </Box>

                </Box>
                {/* ══ End Mobile Hero ══ */}

                {/* Page layout: Banner (left) + animated right visual — desktop only (sm+) */}
                <Box sx={{ ...styles.pageLayout, px: { xs: 4, sm: 8 }, display: { xs: "none", sm: "flex" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "flex-start", width: "100%", height: { xs: "100%", sm: "auto" }, position: "relative" }}>
                  <Banner bannerContent={item.content} />

                  {/* Moving rings across the banner (appear on md+) - About-hero style */}
                  <Box sx={{ display: { xs: "none", md: "block" }, position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
                    {/* Left solid ring (moves left->right) */}
                    <Box sx={{
                      position: "absolute",
                      top: "28%",
                      left: "-22%",
                      width: { md: 160, lg: 180 },
                      height: { md: 160, lg: 180 },
                      borderRadius: "50%",
                      border: "3px solid rgba(91,124,250,0.14)",
                      background: "transparent",
                      boxShadow: "0 8px 30px rgba(91,124,250,0.03)",
                      animation: `${slideLeftToRight} 20s linear infinite`,
                    }} />

                    {/* Right solid ring (moves right->left) */}
                    <Box sx={{
                      position: "absolute",
                      top: "48%",
                      left: "120%",
                      width: { md: 200, lg: 220 },
                      height: { md: 200, lg: 220 },
                      borderRadius: "50%",
                      border: "2px solid rgba(118,75,162,0.12)",
                      background: "transparent",
                      boxShadow: "0 8px 30px rgba(118,75,162,0.03)",
                      animation: `${slideRightToLeft} 26s linear infinite`,
                    }} />
                  </Box>

                  {/* Glassy bubbles animation (visible on all screens) */}
                  <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
                    {/* Bottom to Top bubbles */}
                    {/* Blue gradient bubble */}
                    <Box sx={{
                      position: "absolute",
                      bottom: "-80px",
                      left: "10%",
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.4) 0%, rgba(118,75,162,0.3) 100%)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 8px 32px rgba(91,124,250,0.2)",
                      animation: `${floatBubbleUp} 15s ease-in-out infinite`,
                    }} />
                    {/* White glassy bubble */}
                    <Box sx={{
                      position: "absolute",
                      bottom: "-100px",
                      left: "70%",
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.4)",
                      backdropFilter: "blur(15px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      boxShadow: "0 8px 32px rgba(255,255,255,0.3)",
                      animation: `${floatBubbleUp} 18s ease-in-out infinite 3s`,
                    }} />
                    {/* Pure glossy purple */}
                    <Box sx={{
                      position: "absolute",
                      bottom: "-60px",
                      left: "40%",
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      background: "rgba(118,75,162,0.45)",
                      backdropFilter: "blur(12px)",
                      boxShadow: "0 8px 32px rgba(118,75,162,0.3)",
                      animation: `${floatBubbleUp} 20s ease-in-out infinite 7s`,
                    }} />

                    {/* Top to Bottom bubbles */}
                    {/* White glassy bubble */}
                    <Box sx={{
                      position: "absolute",
                      top: "-80px",
                      left: "25%",
                      width: "70px",
                      height: "70px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.35)",
                      backdropFilter: "blur(18px)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      boxShadow: "0 8px 32px rgba(255,255,255,0.25)",
                      animation: `${floatBubbleDown} 16s ease-in-out infinite 1s`,
                    }} />
                    {/* Pure glossy blue */}
                    <Box sx={{
                      position: "absolute",
                      top: "-60px",
                      left: "80%",
                      width: "55px",
                      height: "55px",
                      borderRadius: "50%",
                      background: "rgba(91,124,250,0.4)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 8px 32px rgba(91,124,250,0.3)",
                      animation: `${floatBubbleDown} 19s ease-in-out infinite 5s`,
                    }} />
                    {/* Purple to blue gradient */}
                    <Box sx={{
                      position: "absolute",
                      top: "-90px",
                      left: "55%",
                      width: "75px",
                      height: "75px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, rgba(118,75,162,0.35) 0%, rgba(91,124,250,0.35) 100%)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 8px 32px rgba(118,75,162,0.25)",
                      animation: `${floatBubbleDown} 17s ease-in-out infinite 9s`,
                    }} />

                    {/* Left to Right bubbles */}
                    {/* Pure glossy purple */}
                    <Box sx={{
                      position: "absolute",
                      top: "20%",
                      left: "-70px",
                      width: "65px",
                      height: "65px",
                      borderRadius: "50%",
                      background: "rgba(118,75,162,0.42)",
                      backdropFilter: "blur(8px)",
                      boxShadow: "0 8px 32px rgba(118,75,162,0.3)",
                      animation: `${floatBubbleRight} 22s ease-in-out infinite 2s`,
                    }} />
                    {/* Blue to purple gradient */}
                    <Box sx={{
                      position: "absolute",
                      top: "60%",
                      left: "-80px",
                      width: "70px",
                      height: "70px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.4) 0%, rgba(118,75,162,0.35) 100%)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 8px 32px rgba(91,124,250,0.25)",
                      animation: `${floatBubbleRight} 20s ease-in-out infinite 6s`,
                    }} />
                    {/* White glassy bubble */}
                    <Box sx={{
                      position: "absolute",
                      top: "40%",
                      left: "-60px",
                      width: "55px",
                      height: "55px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.38)",
                      backdropFilter: "blur(14px)",
                      border: "1px solid rgba(255,255,255,0.28)",
                      boxShadow: "0 8px 32px rgba(255,255,255,0.28)",
                      animation: `${floatBubbleRight} 18s ease-in-out infinite 10s`,
                    }} />

                    {/* Right to Left bubbles */}
                    {/* Blue to purple gradient */}
                    <Box sx={{
                      position: "absolute",
                      top: "25%",
                      right: "-75px",
                      width: "70px",
                      height: "70px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.38) 0%, rgba(118,75,162,0.38) 100%)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 8px 32px rgba(91,124,250,0.25)",
                      animation: `${floatBubbleLeft} 21s ease-in-out infinite 4s`,
                    }} />
                    {/* White glassy bubble */}
                    <Box sx={{
                      position: "absolute",
                      top: "55%",
                      right: "-65px",
                      width: "60px",
                      height: "60px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.42)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255,255,255,0.32)",
                      boxShadow: "0 8px 32px rgba(255,255,255,0.3)",
                      animation: `${floatBubbleLeft} 19s ease-in-out infinite 8s`,
                    }} />
                    {/* Pure glossy blue */}
                    <Box sx={{
                      position: "absolute",
                      top: "70%",
                      right: "-70px",
                      width: "65px",
                      height: "65px",
                      borderRadius: "50%",
                      background: "rgba(91,124,250,0.43)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 8px 32px rgba(91,124,250,0.28)",
                      animation: `${floatBubbleLeft} 23s ease-in-out infinite 11s`,
                    }} />
                  </Box>

                  {/* Right animated visual (shows on md+) - choose per slide */}
                  <Box
                    sx={{
                      display: { xs: "none", md: "flex" },
                      alignItems: "center",
                      justifyContent: "center",
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: { md: "45%", lg: "40%" },
                      pointerEvents: "none",
                    }}
                  >
                    {/* Unified sonar cluster — desktop only, per-slide colours from mv */}
                    <Box
                      sx={{
                        position: "relative",
                        flexShrink: 0,
                        width: 400,
                        height: 400,
                        animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                      }}
                    >
                      {/* Deep glow blob */}
                      <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 200, height: 200, borderRadius: "50%", background: mv.glow, filter: "blur(52px)", animation: `${glowPulse} 3.5s ease-in-out infinite`, pointerEvents: "none" }} />

                      {/* 3 sonar rings */}
                      {[0, 0.9, 1.8].map((delay, i) => (
                        <Box key={i} sx={{ position: "absolute", top: "50%", left: "50%", width: 120, height: 120, borderRadius: "50%", border: `1.5px solid ${mv.glow}`, animation: `${sonarRing} 2.7s ease-out infinite ${delay}s`, pointerEvents: "none", zIndex: 0 }} />
                      ))}

                      {/* Outer rotating dashed ring */}
                      <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 340, height: 340, borderRadius: "50%", border: `1.5px dashed ${mv.accentGlow}`, animation: `${rotateSlow} 30s linear infinite`, pointerEvents: "none" }} />

                      {/* Inner counter-rotating dashed ring */}
                      <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 240, height: 240, borderRadius: "50%", border: "1px dashed rgba(91,124,250,0.18)", animation: `${rotateSlow} 20s linear infinite reverse`, pointerEvents: "none" }} />

                      {/* Center icon */}
                      <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 120, height: 120, borderRadius: "28px", background: mv.iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "62px", lineHeight: 1, boxShadow: `0 0 72px ${mv.glow}, 0 24px 48px rgba(0,0,0,0.12)`, animation: `${iconEntrance} 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.1s both`, zIndex: 2 }}>
                        {mv.icon}
                      </Box>

                      {/* Bouncing chip boxes at 60° intervals, radius 160px from centre (400px container) */}
                      {mv.chips.map((chip, i) => {
                        const pos = [
                          { top: 171, left: 331 },
                          { top: 310, left: 251 },
                          { top: 310, left: 91  },
                          { top: 171, left: 11  },
                          { top: 32,  left: 91  },
                          { top: 32,  left: 251 },
                        ];
                        return (
                          <Box key={i} sx={{ position: "absolute", top: pos[i].top, left: pos[i].left, width: 58, height: 58, borderRadius: "16px", background: "rgba(255,255,255,0.95)", boxShadow: "0 8px 28px rgba(17,24,39,0.12)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", zIndex: 3, animation: `${floatBall} ${5 + i * 0.3}s ease-in-out infinite ${i * 0.45}s` }}>
                            {chip}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>
    </Box>
  );
}
